// Resolves the room and meal plan to select when the user picks a board class in the board class bar:
// the selected room is kept when it offers the new board class; otherwise the cheapest room offering
// it wins — in both cases with the room's cheapest rate of that board class (the default cancellation
// option, matching the "from +€X" price the bar advertises).
// `configurations` is price-sorted ascending, so the first match is a room's cheapest such rate;
// across rooms, ties keep the first room in page order.
export function selectBoardClassTarget(
  rooms: readonly RoomType[],
  boardClass: BoardClassType,
  selectedRoom: RoomType | undefined
): { room: RoomType; mealPlan: MealPlanType } | undefined {
  const cheapestOf = (room: RoomType) => room.configurations.find((config) => config.boardClass === boardClass);

  if (selectedRoom) {
    const mealPlan = cheapestOf(selectedRoom);
    if (mealPlan) return { room: selectedRoom, mealPlan };
  }

  let target: { room: RoomType; mealPlan: MealPlanType } | undefined;
  rooms.forEach((room) => {
    const mealPlan = cheapestOf(room);
    if (mealPlan && (!target || mealPlan.finalPrice < target.mealPlan.finalPrice)) {
      target = { room, mealPlan };
    }
  });
  return target;
}

// Across one or more board classes, returns the single cheapest room+rate. Used in evo to preselect
// the room/meal plan a meal-plan URL filter points at, without filtering the rooms list.
export function selectCheapestBoardClassTarget(
  rooms: readonly RoomType[],
  boardClasses: readonly BoardClassType[]
): { room: RoomType; mealPlan: MealPlanType } | undefined {
  let target: { room: RoomType; mealPlan: MealPlanType } | undefined;
  boardClasses.forEach((boardClass) => {
    const candidate = selectBoardClassTarget(rooms, boardClass, undefined);
    if (candidate && (!target || candidate.mealPlan.finalPrice < target.mealPlan.finalPrice)) {
      target = candidate;
    }
  });
  return target;
}
